import { GameStateEngine } from '@/core/game-state-engine'
import { HudLocator } from '@/core/hud-locator'
import { drawSourceToImageData } from '@/core/image'
import { ReadingValidator } from '@/core/reading-validator'
import { captureIntervalMs, maxRecognitionCaptureFps } from '@/core/recognition-settings'
import type { CaptureProfile, GameEvent, GameStateSnapshot, MetricKey, RawReading, ValidatedObservation } from '@/domain/types'
import type { GamePreset, HudLocation, PresetRecognitionContext, PresetRecognizer } from '@/presets/types'
import { FrameScheduler, type FrameSchedulerHealth } from './frame-scheduler'

export interface PipelineResult {
  readonly captureGeneration: number
  readonly raw: RawReading
  readonly observation: ValidatedObservation
  readonly state: GameStateSnapshot
  readonly events: readonly GameEvent[]
  readonly hud: HudLocation
}

export const SESSION_START_OCR_HANDOFF_MS = 2_000

export class AnalysisPipeline {
  private readonly locator = new HudLocator()
  private readonly validator: ReadingValidator
  private readonly stateEngine: GameStateEngine
  private readonly recognizer: PresetRecognizer
  private scheduler: FrameScheduler | null = null
  private sessionId = ''
  private captureGeneration = 0
  private diagnosticMetric: MetricKey | null = null
  private lastPrioritySampleAt = 0

  constructor(
    private readonly preset: GamePreset,
    private profile: CaptureProfile,
    private readonly onResult: (result: PipelineResult) => void,
    private readonly onStalled?: (health: FrameSchedulerHealth) => void,
  ) {
    this.validator = new ReadingValidator(preset, profile.recognition.minConfidence)
    this.stateEngine = new GameStateEngine(preset)
    this.recognizer = preset.createRecognizer(profile)
  }

  start(video: HTMLVideoElement, sessionId: string, baseline: GameStateSnapshot | null = null, captureGeneration = 0): void {
    this.stop()
    this.sessionId = sessionId
    this.captureGeneration = captureGeneration
    this.lastPrioritySampleAt = 0
    this.recognizer.resetContinuity?.()
    this.validator.resetBaseline(baseline)
    this.stateEngine.resetBaseline(baseline)
    this.recognizer.requestMetrics?.(['exp', 'level'])
    this.scheduler = new FrameScheduler(video, captureIntervalMs(maxRecognitionCaptureFps(this.profile.recognition)), {
      initialMediaTime: video.currentTime,
      onStalled: (health) => this.onStalled?.(health),
    })
    this.scheduler.start((capturedAt, signal) => this.process(video, capturedAt, { signal }))
  }

  async settle(video: HTMLVideoElement, requestedMetrics: readonly MetricKey[], signal: AbortSignal): Promise<boolean> {
    await this.suspendScheduler()
    if (signal.aborted) return false
    return this.process(video, Date.now(), { mode: 'settlement', requestedMetrics, signal })
  }

  async sampleForSessionStart(video: HTMLVideoElement, requestedMetrics: readonly MetricKey[], signal: AbortSignal): Promise<boolean> {
    await this.suspendScheduler()
    if (signal.aborted) return false
    await this.recognizer.preparePriorityRecognition?.(SESSION_START_OCR_HANDOFF_MS, signal)
    if (signal.aborted) return false
    const capturedAt = Math.max(Date.now(), this.lastPrioritySampleAt + 1)
    this.lastPrioritySampleAt = capturedAt
    return this.process(video, capturedAt, { mode: 'settlement', requestedMetrics, signal })
  }

  updateProfile(profile: CaptureProfile): void {
    this.profile = profile
    this.validator.setMinimumConfidence(profile.recognition.minConfidence)
    this.scheduler?.setInterval(captureIntervalMs(maxRecognitionCaptureFps(profile.recognition)))
  }

  setDiagnosticMetric(metric: MetricKey | null): void {
    this.diagnosticMetric = metric
  }

  pulse(force = false): boolean {
    return this.scheduler?.pulse(force) ?? false
  }

  health(): FrameSchedulerHealth | null {
    return this.scheduler?.health() ?? null
  }

  setCaptureGeneration(captureGeneration: number): void {
    if (captureGeneration === this.captureGeneration) return
    this.captureGeneration = captureGeneration
    this.recognizer.resetContinuity?.()
  }

  resetBaseline(): void {
    this.recognizer.resetContinuity?.()
    this.validator.resetBaseline()
    this.stateEngine.resetBaseline()
    this.recognizer.requestMetrics?.(['exp', 'level'])
  }

  stop(): void {
    this.scheduler?.stop()
    this.scheduler = null
  }

  dispose(): void {
    this.stop()
    this.recognizer.dispose()
  }

  private async suspendScheduler(): Promise<void> {
    const scheduler = this.scheduler
    scheduler?.stop()
    this.scheduler = null
    try {
      await scheduler?.waitForIdle()
    } catch {
      // A failed scheduled frame must not prevent a priority sample.
    }
  }

  private async process(
    video: HTMLVideoElement,
    capturedAt: number,
    options: Pick<PresetRecognitionContext, 'mode' | 'requestedMetrics' | 'signal'> = {},
  ): Promise<boolean> {
    if (!video.videoWidth || !video.videoHeight || options.signal?.aborted) return false
    const captureGeneration = this.captureGeneration
    const bitmap = await createImageBitmap(video)
    try {
      if (options.signal?.aborted) return false
      const frame = drawSourceToImageData(bitmap, bitmap.width, bitmap.height)
      const hud = this.locator.locate(frame, this.preset)
      const rawResult = await this.recognizer.recognize({
        frame,
        profile: this.profile,
        capturedAt,
        hudRect: hud.rect,
        diagnosticMetric: this.diagnosticMetric,
        ...options,
      })
      if (captureGeneration !== this.captureGeneration || options.signal?.aborted) return false
      const raw: RawReading = { ...rawResult, diagnostics: { ...rawResult.diagnostics, hudConfidence: hud.confidence } }
      const observation = this.validator.validate(raw)
      const { state, events } = this.stateEngine.apply(this.sessionId, observation)
      this.onResult({ captureGeneration, raw, observation, state, events, hud })
      return true
    } finally {
      bitmap.close()
    }
  }
}
